import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { listRoles, type Role } from '../../api/rolesApi.js';
import {
  assignEmpRole,
  assignEmpTerritory,
  assignManager,
  deactivateEmployee,
  employeeLabelOf,
  EMPLOYEE_STATUSES,
  listEmployees,
  listTerritories,
  MAPPING_TYPES,
  REPORTING_TYPES,
  ROLE_TYPES,
  transitionEmpStatus,
  type EmployeeRow,
  type EmployeeStatus,
  type MappingType,
  type ReportingType,
  type RoleType,
  type TerritoryRow,
} from '../../api/orgApi.js';
import { Field, SelectField, sx, toOptions, todayIso } from './orgKit.js';

type Panel = 'manager' | 'territory' | 'role' | 'status' | null;

export function EmployeeActionsBar(props: { empExtId: string; onChanged: () => void }): ReactNode {
  const { empExtId, onChanged } = props;
  const { token } = useAuth();
  const { t } = useTranslation();
  const [panel, setPanel] = useState<Panel>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [territories, setTerritories] = useState<TerritoryRow[]>([]);
  const [roles, setRoles] = useState<readonly Role[]>([]);
  // Apart from `error`, which opening a panel clears — the very moment the
  // empty picker would be seen.
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Panel form fields
  const [managerId, setManagerId] = useState('');
  const [reportingType, setReportingType] = useState<ReportingType>('PRIMARY');
  const [territoryId, setTerritoryId] = useState('');
  const [mappingType, setMappingType] = useState<MappingType>('PRIMARY');
  const [roleId, setRoleId] = useState('');
  const [roleType, setRoleType] = useState<RoleType>('PRIMARY');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [effectiveToDate, setEffectiveToDate] = useState('');
  const [toStatus, setToStatus] = useState<EmployeeStatus>('ACTIVE');
  const [statusReason, setStatusReason] = useState('');

  useEffect(() => {
    const failed = (e: unknown) => setLookupError((e as Error).message);
    listEmployees(token).then((r) => setEmployees(r.data)).catch(failed);
    listTerritories(token).then((r) => setTerritories(r.data)).catch(failed);
    listRoles(token).then((r) => setRoles(r)).catch(failed);
  }, [token]);

  function togglePanel(p: Panel): void {
    setError(null);
    setNotice(null);
    setPanel((cur) => (cur === p ? null : p));
  }

  async function run(action: () => Promise<unknown>, okMsg: string): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await action();
      setNotice(okMsg);
      setPanel(null);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const managerOptions = employees
    .filter((e) => e.empExtId !== empExtId)
    .map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));
  const territoryOptions = territories.map((t) => ({ value: t.territoryId, label: `${t.territoryCode} — ${t.territoryName}` }));
  const roleOptions = roles.map((r) => ({ value: r.roleId, label: r.roleName }));

  return (
    <section aria-label="employee-actions" style={{ margin: '8px 0 24px' }}>
      {lookupError ? <p role="alert" style={sx.error}>{lookupError}</p> : null}
      {error ? <p role="alert" style={sx.error}>{error}</p> : null}
      {notice ? <p style={sx.ok}>{notice}</p> : null}

      <div style={sx.actionBar}>
        <Link to={`/org/employees/${empExtId}/edit`} style={sx.secondaryBtn} aria-label="edit-employee">{t('org.employee.actions.edit')}</Link>
        <Button type="button" variant="secondary" aria-label="assign-manager-toggle" onClick={() => togglePanel('manager')}>{t('org.employee.actions.assignManager')}</Button>
        <Button type="button" variant="secondary" aria-label="assign-territory-toggle" onClick={() => togglePanel('territory')}>{t('org.employee.actions.assignTerritory')}</Button>
        <Button type="button" variant="secondary" aria-label="assign-role-toggle" onClick={() => togglePanel('role')}>{t('org.employee.actions.assignRole')}</Button>
        <Button type="button" variant="secondary" aria-label="change-status-toggle" onClick={() => togglePanel('status')}>{t('org.employee.actions.changeStatus')}</Button>
        <Link to={`/org/transfers/new?employeeId=${empExtId}`} style={sx.secondaryBtn} aria-label="transfer-employee">{t('org.employee.actions.transfer')}</Link>
        <Link to="/org/successors" style={sx.secondaryBtn} aria-label="successor-employee">{t('org.employee.actions.successor')}</Link>
        <Button
          type="button"
          variant="danger"
          aria-label="deactivate-employee"
          disabled={busy}
          onClick={() => {
            if (window.confirm(t('org.employee.actions.confirmDeactivate'))) {
              void run(() => deactivateEmployee(token, empExtId), t('org.employee.actions.deactivated'));
            }
          }}
        >
          {t('org.employee.actions.deactivate')}
        </Button>
      </div>

      {panel === 'manager' && (
        <div style={sx.card} aria-label="assign-manager-panel">
          <h3>{t('org.employee.actions.assignManager')}</h3>
          <SelectField label={t('org.field.manager')} name="managerId" value={managerId} onChange={setManagerId} options={managerOptions} required />
          <SelectField label={t('org.field.reportingType')} name="reportingType" value={reportingType} onChange={(v) => setReportingType(v as ReportingType)} options={toOptions(REPORTING_TYPES)} required />
          <Field label={t('org.field.effectiveFrom')} name="managerEffectiveFrom" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
          <div style={sx.actionBar}>
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              aria-label="assign-manager-save"
              onClick={() => {
                if (!managerId) { setError(t('org.employee.actions.selectManager')); return; }
                void run(() => assignManager(token, empExtId, { managerId, reportingType, effectiveFromDate }), t('org.employee.actions.managerAssigned'));
              }}
            >
              {busy ? t('org.kit.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      )}

      {panel === 'territory' && (
        <div style={sx.card} aria-label="assign-territory-panel">
          <h3>{t('org.employee.actions.assignTerritory')}</h3>
          <SelectField label={t('org.field.territory')} name="territoryId" value={territoryId} onChange={setTerritoryId} options={territoryOptions} required />
          <SelectField label={t('org.field.mappingType')} name="mappingType" value={mappingType} onChange={(v) => setMappingType(v as MappingType)} options={toOptions(MAPPING_TYPES)} required />
          <Field label={t('org.field.effectiveFrom')} name="territoryEffectiveFrom" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
          <div style={sx.actionBar}>
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              aria-label="assign-territory-save"
              onClick={() => {
                if (!territoryId) { setError(t('org.employee.actions.selectTerritory')); return; }
                void run(() => assignEmpTerritory(token, empExtId, { territoryId, mappingType, effectiveFromDate }), t('org.employee.actions.territoryAssigned'));
              }}
            >
              {busy ? t('org.kit.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      )}

      {panel === 'role' && (
        <div style={sx.card} aria-label="assign-role-panel">
          <h3>{t('org.employee.actions.assignRole')}</h3>
          <SelectField label={t('org.field.role')} name="roleId" value={roleId} onChange={setRoleId} options={roleOptions} required />
          <SelectField label={t('org.field.roleType')} name="roleType" value={roleType} onChange={(v) => setRoleType(v as RoleType)} options={toOptions(ROLE_TYPES)} required />
          <Field label={t('org.field.effectiveFrom')} name="roleEffectiveFrom" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
          <Field label={t('org.field.effectiveTo')} name="roleEffectiveTo" value={effectiveToDate} onChange={setEffectiveToDate} type="date" />
          <div style={sx.actionBar}>
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              aria-label="assign-role-save"
              onClick={() => {
                if (!roleId) { setError(t('org.employee.actions.selectRole')); return; }
                void run(() => assignEmpRole(token, empExtId, { roleId, roleType, effectiveFromDate, effectiveToDate: effectiveToDate || undefined }), t('org.employee.actions.roleAssigned'));
              }}
            >
              {busy ? t('org.kit.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      )}

      {panel === 'status' && (
        <div style={sx.card} aria-label="change-status-panel">
          <h3>{t('org.employee.actions.changeStatus')}</h3>
          <SelectField label={t('org.field.newStatus')} name="toStatus" value={toStatus} onChange={(v) => setToStatus(v as EmployeeStatus)} options={toOptions(EMPLOYEE_STATUSES)} required />
          <Field label={t('org.field.reason')} name="statusReason" value={statusReason} onChange={setStatusReason} />
          <div style={sx.actionBar}>
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              aria-label="change-status-save"
              onClick={() => void run(() => transitionEmpStatus(token, empExtId, { toStatus, reason: statusReason.trim() || undefined }), t('org.employee.actions.statusUpdated'))}
            >
              {busy ? t('org.kit.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
